/**
 * The names a reader's place in an Exercise 练习 travels under, between the
 * page and the `/user/lianxi/` handler.
 *
 * A file of constants with no imports, because both ends need every one of
 * them and neither may import the other. `src/site/lianxi-recorder.ts` posts
 * them from the browser bundle, and a bundle that reached `src/user/` would
 * carry the handler's chrome with it. `src/user/lianxi-address.ts` holds the
 * handler's own addresses beside these.
 *
 * Every press on the page posts the Exercise's slug and says one of three
 * things. On a writing drill, a pass done in a stage that snaps, the ink of a
 * pass written from memory for the handler to mark, or that the reader started
 * the Exercise again. On a grammar exercise, an answer on an item for the
 * handler to mark, or the same start again. docs/lianxi.md under "Keeping a
 * reader's place" has the exchange.
 */

/** The Exercise, by its slug under `/lianxi/`. */
export const exerciseField = "exercise";

/** The character the pass was on. */
export const characterField = "character";

/**
 * The stage the pass was in, as its index, for a pass done in a stage that
 * snaps. A report whose stage is not the kept one is stale and moves nothing.
 */
export const stageField = "stage";

/**
 * The ink of a pass written from memory, as `shapeText` writes it, for the
 * handler to mark. The same format a 复习 handwriting card posts.
 */
export const inkField = "ink";

/**
 * Present, with any value, beside ink the page sent to be marked on its own,
 * because the reader had written as many strokes as the character has. The
 * handler keeps such a pass where it passes and moves nothing where it does
 * not, since the reader asked for no marking and may not have finished.
 */
export const automaticField = "automatic";

/** Present, with any value, where the reader pressed Do the drill again. */
export const againField = "again";

/**
 * The item a grammar exercise's answer is on, as its index in the exercise's
 * order. A report on an item that is not at the head of the kept queue is
 * stale and moves nothing.
 */
export const itemField = "item";

/**
 * The question a reading Exercise's answer is on, as its index in the order
 * asked. The option chosen goes in {@link chosenField}. A report on a question
 * that is not at the head of the kept queue is stale and moves nothing.
 */
export const questionField = "question";

/** The published material and saved queue an answer was made against. */
export const versionField = "version";
export const revisionField = "revision";
export const itemNameField = "itemName";
export const versionAttribute = "cbLianxiVersion";
export const revisionAttribute = "cbLianxiRevision";
export const itemNameAttribute = "cbYufaName";

/** What was typed into a gap item or a Transform item, for the handler to mark. */
export const typedField = "typed";

/**
 * The sentence a Choose item's reader picked, as its place in the order the
 * item shows them, for the handler to mark against the right one.
 */
export const chosenField = "chosen";

/**
 * The parts a 拼字 Exercise's reader chose for one character, written one
 * after another in the order chosen.
 */
export const partsField = "parts";

/**
 * A word said aloud on a 语音答题 Exercise: 16-bit samples at 16 kHz, base64
 * encoded, as `src/site/hold-to-speak.ts` gives them. Heard and dropped by
 * the handler, never kept.
 */
export const spokenField = "spoken";

/** The selected repair piece, or the piece count for No change needed. */
export const repairLocationField = "repairLocation";

/**
 * The pieces of an arrangement in the order the reader put them, one per
 * line, for the handler to mark against the sentence's own order.
 */
export const arrangedField = "arranged";

/**
 * What the block says about itself to the script, under `data-cb-lianxi-`
 * names, on the handler's page alone.
 *
 * `record` is where a press posts, and its presence is what tells the script
 * the page keeps a place. `exercise` is the slug every post names.
 */
export const recordAttribute = "cbLianxiRecord";
export const exerciseAttribute = "cbLianxiExercise";

/**
 * Present, with any value, where the finished Exercise's material has been
 * handed to 复习, so a finished drill opened again says so over the square.
 * A press's answer says the same with `handedOver`.
 */
export const handedOverAttribute = "cbLianxiHandedOver";

/**
 * How many cards the hand-over put in 复习, as
 * `data-cb-lianxi-handover-cards`, and when the first comes due, as an ISO
 * 8601 instant in `data-cb-lianxi-handover-due`, so the finish line can name
 * both (#1209). A press's answer says the same with `handedOverCards` and
 * `handedOverDueAt`. Absent on a row handed over before they were counted.
 */
export const handoverCardsAttribute = "cbLianxiHandoverCards";
export const handoverDueAttribute = "cbLianxiHandoverDue";

/**
 * Present where the reader holds no Pro 专业, as `data-cb-lianxi-free`, so the
 * finish says what a free account's 复习 does with what it handed over. The
 * place answer says the same by carrying `startsLeft`.
 */
export const freeAttribute = "cbLianxiFree";

/**
 * Present where a free reader has no new Exercise left to start today, as
 * `data-cb-lianxi-limited`. The page then runs the taste a visitor is given
 * and keeps nothing. The place answer says the same with `limited`.
 */
export const limitedAttribute = "cbLianxiLimited";

/**
 * Where a reader is in a grammar exercise, on the block, as
 * `data-cb-lianxi-queue` and `data-cb-lianxi-missed`. The queue is the items
 * still to be answered right, by index, in the order they are asked, and the
 * missed are the items missed at least once. A press's answer carries the
 * same two lists as `queue` and `missed`.
 */
export const queueAttribute = "cbLianxiQueue";
export const missedAttribute = "cbLianxiMissed";

/**
 * The option a reader chose for each question of a reading Exercise, as
 * `data-cb-lianxi-choices`, `-1` for a question not answered. The finish's
 * review shows them (CB-40). A press's answer carries them as `choices`.
 */
export const choicesAttribute = "cbLianxiChoices";

/**
 * A reading Exercise's written replies (CB-77): the reply last written to
 * each question as JSON, the tries made at each and the replies left
 * unchecked, as `data-cb-lianxi-replies`, `-tries` and `-unchecked`. A press's
 * answer carries them as `replies`, `tries` and `unchecked`.
 */
export const repliesAttribute = "cbLianxiReplies";
export const triesAttribute = "cbLianxiTries";
export const uncheckedAttribute = "cbLianxiUnchecked";

/**
 * The option chosen for each question, from an answer's `choices` or the
 * block's attribute split, or nothing where it is not one per question.
 * @param value - What was read.
 * @param count - How many questions the Exercise asks.
 * @returns The options, -1 for a question with none known.
 */
export function choicesFrom(
  value: unknown,
  count: number,
): readonly number[] | undefined {
  return Array.isArray(value) &&
    value.length === count &&
    value.every((each) => Number.isInteger(each) && Number(each) >= -1)
    ? (value as number[])
    : undefined;
}

/**
 * Where each character stands, on its item in the row over the square, as
 * `data-cb-xiezi-place-stage` and `data-cb-xiezi-place-pass`. Absent on the
 * public page, where every character starts at the beginning. Named apart from
 * `data-cb-xiezi-stage`, which is the line over the square.
 */
export const stageAttribute = "cbXieziPlaceStage";
export const passAttribute = "cbXieziPlacePass";
